import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(authService.getToken());
  const [tierLimits, setTierLimits] = useState({});

  // Set up axios interceptor to include token
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup function to eject the interceptor
    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  // Check if user is authenticated on component mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const userData = await authService.getCurrentUser(token);
          setUser(userData);
          // Extract tier_limits if available
          if (userData.tier_limits) {
            setTierLimits(userData.tier_limits);
          }
        } catch (error) {
          console.error('Authentication check failed:', error);
          authService.removeToken();
          setToken(null);
          setUser(null);
          setTierLimits({});
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const data = await authService.login(email, password);

      authService.storeToken(data.access_token);
      setToken(data.access_token);
      setUser(authService.parseUserFromToken(data));

      // Extract tier_limits if available
      if (data.tier_limits) {
        setTierLimits(data.tier_limits);
      }

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const register = async (email, password, promoCode = null) => {
    try {
      const data = await authService.register(email, password, promoCode);

      // Registration requires email verification - no token yet
      // Return the success message from backend
      return {
        success: true,
        message: data.message || 'Registration successful! Please check your email to verify your account.',
        requiresVerification: !data.email_verified
      };
    } catch (error) {
      console.error('Registration failed:', error);
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    if (!token) return null;
    try {
      const userData = await authService.getCurrentUser(token);
      setUser(userData);
      if (userData.tier_limits) {
        setTierLimits(userData.tier_limits);
      }
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      authService.removeToken();
      setToken(null);
      setUser(null);
      setTierLimits({});
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    tierLimits,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};